import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ActionButton, Pill } from "@/components/shared";

const EMAIL_TONE = { sent: "success", disabled: "info", skipped_no_email: "warning", error: "danger" };

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right font-medium">{value}</span>
    </div>
  );
}

export default function PublishSendDialog({ open, onClose, assignmentId, clientId, onPublished }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [dueMode, setDueMode] = useState("none"); // none | fixed
  const [dueDate, setDueDate] = useState("");
  const [grace, setGrace] = useState(0);
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const loadEmail = useCallback(async (due) => {
    try {
      const { data } = await api.get(`/strategic-review/assignments/${assignmentId}/email-preview`, { params: due ? { due_date: due } : {} });
      setSubject(data.email.subject); setBodyText(data.email.body);
      return data.email;
    } catch { return null; }
  }, [assignmentId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rev, st] = await Promise.all([
        api.get(`/strategic-review/assignments/${assignmentId}/publication-review`),
        api.get(`/strategic-review/assignments/${assignmentId}/publication-status`),
      ]);
      setSummary(rev.data.summary);
      setStatus(st.data.published ? st.data : null);
      if (st.data.published && st.data.due_date) { setDueMode("fixed"); setDueDate(String(st.data.due_date).slice(0, 10)); }
      await loadEmail(st.data.due_date);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load publication data"); }
    finally { setLoading(false); }
  }, [assignmentId, loadEmail]);

  useEffect(() => { if (open) loadAll(); }, [open, loadAll]);

  const effectiveDue = dueMode === "fixed" ? dueDate : "";

  const publish = async () => {
    if (dueMode === "fixed" && !dueDate) { toast.error("Select a due date or choose 'No due date'"); return; }
    if (dueMode === "fixed" && dueDate < today) { toast.error("Due date cannot be earlier than today"); return; }
    setPublishing(true);
    try {
      const { data } = await api.post(`/strategic-review/assignments/${assignmentId}/publish-and-send`, {
        due_date: effectiveDue, grace_period_days: Number(grace) || 0,
        email_subject: subject, email_body: bodyText,
      });
      if (data.already) toast.info("This review has already been published & sent");
      else toast.success(`Published & sent — email ${data.email?.status}`);
      onPublished?.();
      await loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const retryEmail = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/strategic-review/assignments/${assignmentId}/retry-email`, { email_subject: subject, email_body: bodyText });
      toast.success(`Email retried — ${data.email?.status}`);
      await loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Retry failed"); }
    finally { setRetrying(false); }
  };

  const published = !!status?.published;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sr-publish-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.Send className="h-5 w-5 text-teal-600" />Publish &amp; Send to Client</DialogTitle>
          <DialogDescription>Review the questionnaire, set a due date, preview the client email, then publish and notify the client.</DialogDescription>
        </DialogHeader>

        {loading ? <div className="py-10 text-center text-slate-400">Loading…</div> : (
          <div className="space-y-5">
            {published && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="sr-publish-published-banner">
                <div className="flex items-center gap-2 text-emerald-700 font-medium"><Icons.CheckCircle2 className="h-4 w-4" />Published &amp; sent{status.published_and_sent_at ? ` · ${new Date(status.published_and_sent_at).toLocaleString()}` : ""}</div>
                <div className="mt-1 text-slate-600">Email: <Pill tone={EMAIL_TONE[status.email?.status] || "neutral"}>{status.email?.status}</Pill>{status.email?.error ? <span className="text-rose-500 ml-2 text-xs">{status.email.error}</span> : null}</div>
              </div>
            )}

            {/* 1. Publication Review */}
            <section data-testid="sr-publication-review">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Publication Review</h3>
              <div className="rounded-lg border border-slate-200 p-3">
                <Row label="Client" value={summary?.client_name || summary?.client_id} />
                <Row label="Email" value={summary?.client_email || <span className="text-rose-500">none on file</span>} />
                <Row label="Template" value={`${summary?.template_title || summary?.template_code} (v${summary?.template_version ?? "—"})`} />
                <Row label="Questionnaire version" value={summary?.questionnaire_version ?? "—"} />
                <Row label="Modules" value={`${summary?.module_count} — ${(summary?.modules || []).join(", ")}`} />
                <Row label="Jurisdictions" value={(summary?.jurisdictions || []).join(", ") || "—"} />
                <Row label="Questions" value={summary?.question_count} />
                <Row label="Document requests" value={summary?.document_request_count} />
                <Row label="Rounds" value={summary?.round_count} />
              </div>
              <button onClick={() => navigate(`/client-success/strategic-review/${clientId}/preview/${assignmentId}`)}
                className="mt-2 text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="sr-open-client-preview">
                <Icons.Eye className="h-4 w-4" />Open full Client Preview
              </button>
            </section>

            {/* 2. Due date */}
            <section data-testid="sr-due-date">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Due Date</h3>
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-sm">
                  <input type="radio" checked={dueMode === "none"} onChange={() => setDueMode("none")} data-testid="sr-due-none" />No due date
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm">
                  <input type="radio" checked={dueMode === "fixed"} onChange={() => setDueMode("fixed")} data-testid="sr-due-fixed" />Fixed date
                </label>
                {dueMode === "fixed" && (
                  <Input type="date" min={today} value={dueDate} onChange={(e) => { setDueDate(e.target.value); loadEmail(e.target.value); }} className="w-44" data-testid="sr-due-input" />
                )}
                {dueMode === "fixed" && dueDate === today && <span className="text-xs text-amber-600">This review will be due today.</span>}
              </div>
              {dueMode === "fixed" && (
                <div className="mt-2 text-xs text-slate-500">Reminders: 7 &amp; 3 days before, on the due date, then 7 &amp; 14 days overdue. Auto-cancelled when the client submits.</div>
              )}
              {dueMode === "none" && <div className="mt-2 text-xs text-slate-400">No due date — no reminders scheduled. The client can complete it any time.</div>}
            </section>

            {/* 3. Email preview */}
            <section data-testid="sr-email-preview">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Email Preview</h3>
              <div className="space-y-2">
                <div className="text-xs text-slate-500">To: {summary?.client_email || "no recipient on file"}</div>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="sr-email-subject" />
                <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={7} className="font-mono text-xs" data-testid="sr-email-body" />
                <div className="flex flex-wrap gap-1">
                  {["client_name", "firm_name", "review_title", "portal_link", "due_date"].map((f) => <Badge key={f} variant="secondary" className="text-[9px]">{`{{${f}}}`}</Badge>)}
                  <span className="text-[10px] text-slate-400">System merge fields are resolved on send.</span>
                </div>
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              {published && (
                <ActionButton variant="secondary" icon="RefreshCw" onClick={retryEmail} loading={retrying} data-testid="sr-retry-email">Retry Email</ActionButton>
              )}
              <ActionButton variant="ghost" onClick={onClose} data-testid="sr-publish-cancel">Close</ActionButton>
              <ActionButton variant="primary" icon="Send" onClick={publish} loading={publishing} data-testid="sr-publish-confirm">
                {published ? "Re-send / Update" : "Publish & Send"}
              </ActionButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
